import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'

import { ApiService, DealerMasterKey, DealerMasterKeyRequest } from '@overtur-monorepo/keying-shared'
import { HierarchyConfig } from '../../configuration/keying.hierarchy.environment.config'
import { HttpHeaders } from '@angular/common/http'

@Injectable({
  providedIn: 'root',
})
export class DealerMasterKeyService {
  dealerSpecFormData = new BehaviorSubject<DealerMasterKeyRequest[]>([])
  dealerSpecSaveAndContinueFormData = new BehaviorSubject<DealerMasterKeyRequest[]>([])
  keySectionPropagation$ = new BehaviorSubject<{ masterKeyIds: number[]; keySection: any; keySectionId: number } | null>(null)

  constructor(private api: ApiService, private config: HierarchyConfig) {}

  getDealerMasterKeys(structureId: number, keySystemId: number): Observable<DealerMasterKey[]> {
    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}/DealerSpecs`
    // let httpHeaders = new HttpHeaders().set('Accept', 'application/allegion.keying.dealer.masterkey+json')
    return this.api.get<DealerMasterKey[]>(this.buildUrl(serviceEndpoint))
  }

  getDealerConstructionKeyBitting(structureId: number, keySystemId: number): Observable<string[]> {
    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}/masterKeys`
    let httpHeaders = new HttpHeaders().set('Content-type', 'application/allegion.keying.get.dealer.ck.bitting+json')
    return this.api.get<string[]>(this.buildUrl(serviceEndpoint), httpHeaders)
  }

  createDealerMasterKey(
    structureId: number,
    keySystemId: number,
    masterKeyId: number,
    request: DealerMasterKeyRequest
  ): Observable<DealerMasterKey> {
    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}`
    const httpHeaders = new HttpHeaders().set(
      'Content-type',
      'application/allegion.keying.upsert.dealer.masterkey+json'
    )
    return this.api.put<DealerMasterKey, any>(this.buildUrl(serviceEndpoint), request, httpHeaders)
  }

  updateDealerMasterKey(
    structureId: number,
    keySystemId: number,
    masterKeyId: number,
    request: DealerMasterKeyRequest
  ): Observable<any> {
    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}/masterKeys/${masterKeyId}`
    const httpHeaders = new HttpHeaders().set(
      'Content-type',
      'application/allegion.keying.upsert.dealer.masterkey+json'
    )
    return this.api.put<DealerMasterKey, any>(this.buildUrl(serviceEndpoint), request, httpHeaders)
  }

  resetDealerKeySystem(structureId: number, keySystemId: number): Observable<any> {
    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}`
    const httpHeaders = new HttpHeaders().set('Content-type', 'application/allegion.keying.reset.dealer.keysystem+json')
    return this.api.put<DealerMasterKey, any>(this.buildUrl(serviceEndpoint), null, httpHeaders)
  }

  updateDealerKeySystem(structureId: number, keySystemId: number): Observable<any> {
    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}`
    const httpHeaders = new HttpHeaders().set(
      'Content-type',
      'application/allegion.keying.update.dealer.keysystem+json'
    )
    return this.api.put<DealerMasterKey, any>(this.buildUrl(serviceEndpoint), null, httpHeaders)
  }

  private buildUrl(serviceEndpoint: string): string {
    return `${this.config.baseUrl}/${serviceEndpoint}`
  }

  setDealerSpecFormData(formData: any[]) {
    this.dealerSpecFormData.next(formData)
  }

  setDealerSpecSaveAndContinueFormData(formData: any[]) {
    this.dealerSpecSaveAndContinueFormData.next(formData)
  }

  clearDealerSpecFormData() {
    this.dealerSpecFormData.next([])
  }

  clearDealerSpecSaveAndContinueFormData() {
    this.dealerSpecSaveAndContinueFormData.next([])
  }

  getDealerSpecSaveAndContinueFormData(): Observable<DealerMasterKeyRequest[]> {
    return this.dealerSpecSaveAndContinueFormData.asObservable()
  }

  getDealerSpecFormData(): Observable<DealerMasterKeyRequest[]> {
    return this.dealerSpecFormData.asObservable()
  }

  propagateKeySection(masterKeyIds: number[], keySection: any, keySectionId: number) {
    this.keySectionPropagation$.next({ masterKeyIds, keySection, keySectionId })
  }

  validateDealerSpecs(structureId: number, keySystemId: number, request: DealerMasterKeyRequest[]): Observable<any> {
    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}/DealerSpecs/validate`
    return this.api.post<DealerMasterKey, any>(this.buildUrl(serviceEndpoint), request)
  }

  saveDealerSpecs(structureId: number, keySystemId: number, request: DealerMasterKeyRequest[]): Observable<any> {
    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}/DealerSpecs`
    const httpHeaders = new HttpHeaders().set('Content-type', 'application/allegion.keying.save.dealer.specs+json')
    return this.api.put<DealerMasterKey, any>(this.buildUrl(serviceEndpoint), request, httpHeaders)
  }

  saveandProcessDealerSpecs(
    structureId: number,
    keySystemId: number,
    request: DealerMasterKeyRequest[]
  ): Observable<any> {
    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}/DealerSpecs`
    const httpHeaders = new HttpHeaders().set(
      'Content-type',
      'application/allegion.keying.save.and.process.dealer.specs+json'
    )
    return this.api.put<DealerMasterKey, any>(this.buildUrl(serviceEndpoint), request, httpHeaders)
  }

  reverseDealerSpecs(
    structureId: number,
    keySystemId: number,
    masterKeyId: number,
    request: DealerMasterKeyRequest
  ): Observable<any> {
    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}/DealerSpecs`
    const httpHeaders = new HttpHeaders().set(
      'Content-type',
      'application/allegion.keying.validate.on.inverse.dealer.spec.bittings+json'
    )
    return this.api.put<DealerMasterKey, any>(this.buildUrl(serviceEndpoint), [masterKeyId, request], httpHeaders)
  }

  /* istanbul ignore next */
  importDealerSpec(structureId: number, keySystemId: number, file: File): Observable<any> {
    const allowedFileExtensions = ['.csv', '.xlsx']

    const fileExtension = file[0].name.split('.').pop()
    if (!allowedFileExtensions.includes(`.${fileExtension}`)) {
      throw new Error('Invalid file type. Only CSV and Excel files are supported.')
    }

    const blob = new Blob([file[0]], { type: file[0].type })

    const formData = new FormData()
    formData.append('file', blob, file[0].name)

    const httpHeaders = new HttpHeaders({
      Accept: 'application/json',
    })

    const serviceEndpoint = `structures/${structureId}/keySystems/${keySystemId}/DealerSpecs/import`

    return this.api.post<DealerMasterKey, any>(this.buildUrl(serviceEndpoint), formData, httpHeaders)
  }
}
